import React from "react"
import * as Yup from "yup"
import {Formik} from "formik"
import Dropdown from "../../../Address/Dropdown"
import TextField from "../../../Address/Textfield"
import Card from "../../../../containers/Card/Card"
import DateField from "../../../DateField/DateField"
import AddressComponent from "../../../Address/Address"
import {countries} from "../../../../resources/countries"
import {US_COUNTRY_CODE} from "../../../../resources/address"
import {AnnualIncome, Occupation, SourceOfIncome} from "../../../../resources/application"
import {Address, FullName, Phone} from "../../../../resources/common"
import LeftRightSlots from "../../../../containers/LeftRightSlots/LeftRightSlots"
import officerValidationSchema from "./reevaluateOfficerValidationSchema"
import IdentificationTypeSelect from "../../../IdentificationTypeSelect/ApplicationIdentificationNumber"
import PhoneNumber from "../../../Address/PhoneNumber"
import {ApplicationResubmitForms} from "../../../../pages/ReevaluateApplication/ReevaluatePage"

export interface ReevaluatedOfficerApplicationData {
	fullName: FullName
	address: Address
	dateOfBirth: Date | string
	email: string
	phone: Phone
	ssn?: string
	nationality?: string
	passport?: string
	matriculaConsular?: string
	occupation?: Occupation | ""
	sourceOfIncome?: SourceOfIncome | ""
	annualIncome?: AnnualIncome | ""
}

export default function ReevaluateOfficerForm(props: {
	officer: ReevaluatedOfficerApplicationData
	submitCallback: (values: ReevaluatedOfficerApplicationData) => void
	formId: ApplicationResubmitForms
}) {
	const {officer, submitCallback, formId} = props

	const validationSchema = Yup.object().shape(officerValidationSchema)

	const initialFormValues = {
		fullName: {
			first: officer.fullName.first,
			last: officer.fullName.last,
		},
		address: {
			street: officer.address?.street,
			street2: officer.address?.street2,
			city: officer.address?.city,
			state: officer.address?.state ?? "",
			postalCode: officer.address?.postalCode,
			country: officer.address?.country ?? US_COUNTRY_CODE,
		},
		ssn: officer.ssn ?? "",
		nationality: officer.nationality ?? "US",
		passport: officer.passport ?? "",
		matriculaConsular: officer.matriculaConsular ?? "",
		dateOfBirth: officer.dateOfBirth,
		identificationType: officer.ssn ? "ssn" : officer.matriculaConsular ? "matriculaConsular" : "passport",
		phone: officer.phone,
		email: officer.email,
		annualIncome: officer.annualIncome,
		occupation: officer.occupation,
		sourceOfIncome: officer.sourceOfIncome,
	}

	const title = `Officer - ${officer.fullName.first} ${officer.fullName.last}`

	return (
		<Card className="reevaluate-form" title={title}>
			<Formik
				initialValues={initialFormValues}
				validationSchema={validationSchema}
				onSubmit={(values) => {
					submitCallback({
						...values,
						occupation: values.occupation === "" ? undefined : values.occupation,
						annualIncome: values.annualIncome === "" ? undefined : values.annualIncome,
						sourceOfIncome: values.sourceOfIncome === "" ? undefined : values.sourceOfIncome,
					})
				}}
			>
				{({values, handleSubmit, handleChange, setFieldValue, handleBlur}) => {
					return (
						<form noValidate onSubmit={handleSubmit} id={formId}>
							<LeftRightSlots
								left={
									<>
										<TextField
											label="First Name"
											placeholder="Enter First Name"
											name="fullName.first"
											value={values.fullName.first}
											onChange={handleChange}
											onBlur={handleBlur}
										/>
										<TextField
											label="Last Name"
											placeholder="Enter Last Name"
											name="fullName.last"
											value={values.fullName.last}
											onChange={handleChange}
											onBlur={handleBlur}
										/>

										<IdentificationTypeSelect
											value={values.identificationType}
											onChange={handleChange}
											label="Document Type"
										/>

										{values.identificationType === "matriculaConsular" && (
											<TextField
												label="Document ID #"
												placeholder="Enter Matricula Consular"
												name="matriculaConsular"
												value={values.matriculaConsular}
												onChange={(value) => {
													handleChange(value)
													setFieldValue("ssn", "")
													setFieldValue("passport", "")
												}}
												onBlur={handleBlur}
											/>
										)}

										{values.identificationType === "ssn" && (
											<>
												<Dropdown
													name="nationality"
													label="Nationality"
													value={values.nationality}
													onChange={handleChange}
													onBlur={handleBlur}
												>
													{Object.keys(countries).map((countryCode) => (
														<option key={countryCode} value={countryCode}>
															{countries[countryCode]}
														</option>
													))}
												</Dropdown>
												<TextField
													label="Document ID #"
													placeholder="Enter SSN"
													name="ssn"
													value={values.ssn}
													onChange={(value) => {
														handleChange(value)
														setFieldValue("matriculaConsular", "")
														setFieldValue("passport", "")
													}}
													onBlur={handleBlur}
												/>
											</>
										)}

										{values.identificationType === "passport" ? (
											<>
												<Dropdown
													name="nationality"
													label="Nationality"
													value={values.nationality}
													onChange={handleChange}
													onBlur={handleBlur}
												>
													{Object.keys(countries).map((countryCode) => (
														<option key={countryCode} value={countryCode}>
															{countries[countryCode]}
														</option>
													))}
												</Dropdown>

												<TextField
													label="Document ID #"
													placeholder="Enter Passport"
													name="passport"
													value={values.passport}
													onChange={(value) => {
														handleChange(value)
														setFieldValue("matriculaConsular", "")
														setFieldValue("ssn", "")
													}}
													onBlur={handleBlur}
												/>
											</>
										) : null}

										<DateField
											name="dateOfBirth"
											label="Birth Date"
											value={values.dateOfBirth as string}
											onChange={handleChange}
											onBlur={handleBlur}
										/>
										<TextField
											label="Email"
											placeholder="Enter Email"
											name="email"
											value={values.email ?? ""}
											onChange={(value) => {
												handleChange(value)
											}}
											onBlur={handleBlur}
										/>

										<Dropdown
											name="occupation"
											label="Occupation"
											value={values.occupation}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											{officer.occupation ? <React.Fragment /> : <option value="" />}
											{Object.entries(Occupation).map(([key, value]) => (
												<option key={key} value={key}>
													{value}
												</option>
											))}
										</Dropdown>

										<Dropdown
											name="annualIncome"
											label="Annual Income"
											value={values.annualIncome}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											{officer.annualIncome ? <React.Fragment /> : <option value="" />}
											{Object.entries(AnnualIncome).map(([key, value]) => (
												<option key={key} value={key}>
													{value}
												</option>
											))}
										</Dropdown>

										<Dropdown
											name="sourceOfIncome"
											label="Source Of Income"
											value={values.sourceOfIncome}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											{officer.sourceOfIncome ? <React.Fragment /> : <option value="" />}
											{Object.entries(SourceOfIncome).map(([key, value]) => (
												<option key={key} value={key}>
													{value}
												</option>
											))}
										</Dropdown>

										<PhoneNumber
											fieldClassName={"reevaluate-phone-number-form"}
											name="phoneNumber"
											label="Phone Number"
											countryCodeName="phone.countryCode"
											phoneNumberName="phone.number"
											countryCodeValue={values.phone.countryCode}
											phoneNumberValue={values.phone?.number}
											countryCodeOnChange={(value) => {
												setFieldValue("phone.countryCode", value)
											}}
											phoneNumberOnChange={(value) => {
												setFieldValue("phone.number", value)
											}}
											countryCodeOnBlur={handleBlur}
											phoneNumberOnBlur={handleBlur}
											infoTooltipText="Enter the country code followed by the phone number."
										/>
									</>
								}
								right={
									<AddressComponent
										closed={false}
										namePrefix="address."
										street={values.address.street}
										street2={values.address.street2}
										city={values.address.city}
										country={values.address.country || ""}
										state={values.address.state}
										postalCode={values.address.postalCode}
										isUsOnlyAddress={false}
										handleChange={handleChange}
										handleBlur={handleBlur}
										setFieldValue={setFieldValue}
									/>
								}
							/>
						</form>
					)
				}}
			</Formik>
		</Card>
	)
}
